import React, {useEffect} from "react"
import {UseStores} from "../../../hooks/UseStores"
import {GET_MONTHS_LIST} from "../../../gql/gqlInsights"
import {
  GET_TOP_QUERY,
  GET_TOP_10_BRAND_QUERY,
  GET_TOP_10_CAT_QUERY,
  GET_BRAND_QUERY,
  GET_CAT_QUERY,
  GET_BRAND_WISE_DETAILS,
  GET_CATEGORY_WISE_DETAILS,
} from "../../../gql/gqlBrands"
import {GET_LG, GET_LG_WISE_DETAILS} from "../../../gql/gqlLocGroups"
import {useQuery, useManualQuery} from "graphql-hooks"
import {observer} from "mobx-react-lite"
import {insightClient} from "../../../gql/gqlInterceptor"
import {
  getCurrentTime,
  brandIdToSicCode,
  defaultMapGeos,
  URLPATHS,
} from "../../../utils/utils"

const AppData = () => {
  const {disStore, insightStore, locGroupStore, gtMapStore, poiStore, navStore} =
    UseStores()

  //detect back and forward browser buttons
  //set last geo on discovery/location groups page
  window.onpopstate = (event) => {
    if (window.location.pathname === "/brands/discovery") {
      disStore.setGeoTimeObj({geo: disStore.geoNonePOI})
      gtMapStore.setMapObj({
        geo: disStore.geoNonePOI,
        brandId: disStore.brandId,
        categoryId: disStore.sicCode,
      })
    } else if (window.location.pathname === "/locationgroups") {
      locGroupStore.setLocGeoTime({geo: locGroupStore.geoNonePOI})
      gtMapStore.setMapObj({
        geo: locGroupStore.geoNonePOI,
        locationGroupId: locGroupStore.tagId,
        mapGeos: locGroupStore.mapGeos,
      })
    } else if (window.location.pathname === "/poi") {
      if (navStore.currentPage === URLPATHS.INSIGHTS) {
        //hack to render the POI map correctly from Insights page
        gtMapStore.setMapObj({
          geo: {geoType: "COUNTRY", geoId: 0},
          categoryId: disStore.sicCode,
          brandId: disStore.brandId,
          visitDuration: disStore.timePeriod,
          mapGeos: defaultMapGeos,
          showLoader: true,
        })
      }
      setTimeout(() => {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)
        const id = urlParams.get("id")
        poiStore.setPoiId(Number(id))
      }, 200)
    }
  }

  const {data, loading: monthLoading} = useQuery(GET_MONTHS_LIST, {
    client: insightClient,
  })

  // to load the available months data for brand insights
  useEffect(() => {
    if (data && data.listAvailableMonths) {
      const monthsData = [...data.listAvailableMonths]
      insightStore.setAvailableMonths(monthsData)
      insightStore.setInsightMonth(monthsData[0])
      insightStore.setLastMonth(monthsData[0])
    }
  }, [monthLoading])

  /****************COUNTRY TOP 10 *********************/

  const disGeoTime = disStore.geoTimeObj
  const {geo, timePeriod} = disGeoTime
  // const timePeriod = timePeriodStore.timePeriod;
  const brandId = disStore.brandId
  const category = disStore.sicCode

  let variables, queryType //initialize GQL query args

  if (brandId === null && category === null) {
    //if first render on page
    variables = {geo, timePeriod} //set variables per query req
    queryType = GET_TOP_QUERY
  } else if (category === null) {
    //if user has selected brand
    const sicCode = brandIdToSicCode(brandId)
    variables = {geo, timePeriod, brandId, sicCode} //set variables per query req
    queryType = GET_TOP_10_BRAND_QUERY
  } else if (brandId === null) {
    //if user has selected a category
    const sicCode = category
    variables = {geo, timePeriod, sicCode} //set variables per query req
    queryType = GET_TOP_10_CAT_QUERY
  }

  //set fetch hooks for each scenario - top 10, top 10 and brand, top 10 and category
  const [fetchTop, {error: tableDataError}] = useManualQuery(queryType, {
    variables: variables,
  })
  const [fetchBrand, {error: brandError}] = useManualQuery(GET_BRAND_QUERY, {
    variables: variables,
  })
  const [fetchCat, {error: catError}] = useManualQuery(GET_CAT_QUERY, {
    variables: variables,
  })

  const fetchTopTen = async (canceled) => {
    let resultsTop = disStore.cachedCountryData
    let resultsBrand, resultsCategory //initialize variables for user previous selection brand/category
    const noCacheCountry = resultsTop.length === 0
    const currentHour = new Date().getHours()
    const cachedHour = new Date(disStore.cachedCountryTime).getHours()
    const cacheCountryExpired = currentHour - cachedHour >= 3 // cache expires in 3hrs

    // Only making query when cached is empty, expired OR user clicking new state etc
    if (
      noCacheCountry ||
      cacheCountryExpired ||
      geo.geoType !== "COUNTRY" ||
      timePeriod !== "SEVEN_DAYS"
    ) {
      disStore.setTableStates({tableDataLoading: true})

      const {data} = await fetchTop() //get the top 10 data. loading null, error undefined
      console.log("===top 10===", geo.geoType, data)

      resultsTop = data && data.categories
      resultsBrand = data && brandId !== null ? data.brand : null
      resultsCategory = data && brandId === null ? data.category : null

      // Condition to invalidate old country top 10 cache (set new cachedCountryData)
      if (resultsTop && geo.geoType === "COUNTRY" && timePeriod === "SEVEN_DAYS") {
        disStore.setCachedCountryData(resultsTop, getCurrentTime()) // COUNTRY TOP 10 Cache
      }
    }
    // Fixing the bug of clicking fast. Only do following if 2nd click not cancel the 1st one
    if (resultsTop && !canceled) {
      //on country level and brand selected
      if (
        geo.geoType === "COUNTRY" &&
        category === null &&
        brandId !== null &&
        timePeriod === "SEVEN_DAYS"
      ) {
        disStore.setTableStates({tableDataLoading: true})
        const sicCode = brandIdToSicCode(brandId)
        const isPresent = disStore.isBrandPresent(sicCode, brandId) //check brand presence

        if (!isPresent || disStore.brandSearchBool) {
          //brand not present, fetch it!
          const {data} = await fetchBrand()
          data && data.brand && disStore.handleNewBrandSearch(data.brand) //store selected brand
        } else disStore.handleExistingBrandSearch(sicCode, brandId)
      }
      //on country level and category selected
      else if (
        geo.geoType === "COUNTRY" &&
        category !== null &&
        brandId === null &&
        timePeriod === "SEVEN_DAYS"
      ) {
        disStore.setTableStates({tableDataLoading: true})
        const isPresent = disStore.isCategoryPresent(category) //check cat presence

        if (!isPresent || disStore.catSearchBool) {
          //cat not present or new cat search, fetch it!
          const {data} = await fetchCat()
          data && data.category && disStore.handleNewCategorySearch(data.category)
        } else disStore.handleExistingCategorySearch(category)
      } else disStore.setDataAndId(resultsTop, resultsBrand, resultsCategory) //update tableStates based on results
    }
  }

  const handleFetchError = async () => {
    if (disStore.tableStates.retryCount < 3) {
      let incrementedCount = disStore.tableStates.retryCount + 1
      console.warn("AppData/index.js LINE 134: handleFetchError Retry", incrementedCount)
      disStore.setTableStates({retryCount: incrementedCount}) // increment number of retries
      await fetchTopTen() // retry failed query
    } else {
      // If the retry count exceeds 3,
      disStore.setTableStates({tableDataLoading: false, tableDataError: true})
      disStore.setTableStates({retryCount: 0}) //reset counter after three tries
      console.error(
        "AppData/index.js LINE 142: The query failed after three retires also."
      )
    }
  }

  useEffect(() => {
    if (tableDataError || brandError || catError) {
      handleFetchError()
    }
  }, [tableDataError, brandError, catError])

  useEffect(() => {
    let canceled = false
    if (geo.geoType !== "POI") {
      fetchTopTen(canceled)
    } // if not poi end
    return () => (canceled = true)
  }, [disGeoTime])

  /********************TOP TEN GEO SERVICE REQUEST ********************/

  const topGeoServiceObj = disStore.geoServiceObj
  const [brandsGeoFetchManual] = useManualQuery(GET_BRAND_WISE_DETAILS)
  const [catGeoFetchManual] = useManualQuery(GET_CATEGORY_WISE_DETAILS)

  useEffect(() => {
    let cancelled = false
    let data, error
    if (topGeoServiceObj.selected !== null && geo.geoType !== "POI") {
      ;(async () => {
        disStore.setShowLoader(true) //start map loader
        gtMapStore.setMapObj({showLoader: true})

        if (brandId) {
          ;({data, error} = await brandsGeoFetchManual({
            variables: {
              brandId: brandId,
              timePeriod: timePeriod,
            },
          }))
        } else if (category) {
          ;({data, error} = await catGeoFetchManual({
            variables: {
              sicCode: category,
              timePeriod: timePeriod,
            },
          }))
        }
        if (!error && !cancelled && data.STATE.geos.length !== 0) {
          disStore.setMapGeos(data)
          disStore.setMapGeos({selected: topGeoServiceObj.selected})
          disStore.setMapGeoObj({
            selected: topGeoServiceObj.selected,
            timePeriod: topGeoServiceObj.timePeriod,
          })
          disStore.setShowLoader(false)
        } else if (error) {
          console.error("AppData/index LINE 224: ERROR IN FETCH TOP 10 MAP DATA")
          disStore.setMapGeos(defaultMapGeos)
        } else if (!data.STATE.geos.length || cancelled) {
          disStore.setShowLoader(true)
        }
      })()
    }
    return () => (cancelled = true)
  }, [JSON.stringify(topGeoServiceObj)])

  /**************** LOCATION GROUPS **********************/

  const locGroupGeoTime = locGroupStore.locGeoTime
  const {geo: lgGeo, timePeriod: lgTimePeriod} = locGroupGeoTime
  const {tenantId, accountId} = locGroupStore

  const [fetchLocGroups] = useManualQuery(GET_LG, {
    variables: {tenantId, accountId, geo: lgGeo, timePeriod: lgTimePeriod},
  })

  useEffect(() => {
    let cancelled = false
    if (lgGeo.geoType !== "POI") {
      ;(async () => {
        let resultsTop = locGroupStore.cachedCountryData
        const noCacheCountry = resultsTop.length === 0
        const currentHour = new Date().getHours()
        const cachedHour = new Date(locGroupStore.cachedCountryTime).getHours()
        const cacheCountryExpired = currentHour - cachedHour >= 3 // cache expires in 3hrs

        // Only making query when cached is empty, expired OR user clicking new state etc
        if (
          noCacheCountry ||
          cacheCountryExpired ||
          lgGeo.geoType !== "COUNTRY" ||
          lgTimePeriod !== "SEVEN_DAYS"
        ) {
          locGroupStore.setTableStates({tableDataLoading: true})

          const {data, error: locGroupError} = await fetchLocGroups() //get location group data
          if (
            locGroupError &&
            data &&
            (data.locationGroups == null || data.locationGroups.length === 0)
          ) {
            locGroupStore.setTableStates({tableDataLoading: false, tableDataError: true})
          } else if (locGroupError && !data) {
            locGroupStore.setTableStates({tableDataLoading: false, tableDataError: true})
          } else if (!locGroupError && data && data.locationGroups.length === 0) {
            locGroupStore.setTableStates({tableDataLoading: false, tableDataError: true})
          }

          resultsTop = data && data.locationGroups

          // Condition to invalidate old country cache for location groups (set new cachedCountryData)
          if (
            resultsTop &&
            lgGeo.geoType === "COUNTRY" &&
            lgTimePeriod === "SEVEN_DAYS"
          ) {
            locGroupStore.setCachedCountryData(resultsTop, getCurrentTime())
          }
        }
        if (resultsTop && !cancelled) {
          locGroupStore.setDataAndId(resultsTop) //update tableStates based on results
        }
      })()
    }
    return () => (cancelled = true)
  }, [locGroupGeoTime])

  /********************LOCATION GROUPS GEO SERVICE REQUEST ********************/

  const {geoServiceObj} = locGroupStore
  const [geoFetchManual] = useManualQuery(GET_LG_WISE_DETAILS)

  useEffect(() => {
    let cancelled = false
    if (geoServiceObj.selected !== null && lgGeo.geoType !== "POI") {
      ;(async () => {
        locGroupStore.setShowLoader(true) //start map loader
        gtMapStore.setMapObj({showLoader: true})

        const {data, error} = await geoFetchManual({
          variables: {
            tagId: geoServiceObj.selected,
            accountId: accountId,
            tenantId: tenantId,
            timePeriod: timePeriod,
          },
        })

        if (!error && !cancelled && data.STATE.geos.length !== 0) {
          locGroupStore.setMapGeos(data)
          locGroupStore.setMapGeos({selected: geoServiceObj.selected})
          locGroupStore.setMapGeoObj({
            selected: geoServiceObj.selected,
            timePeriod: geoServiceObj.timePeriod,
          })
          locGroupStore.setShowLoader(false)
        } else {
          console.error("AppData/index LINE 248: ERROR IN FETCH LG MAP DATA")
          locGroupStore.setMapGeos(defaultMapGeos)
        }
      })()
    }
    return () => (cancelled = true)
  }, [JSON.stringify(geoServiceObj)])

  return <></>
}
export default observer(AppData)
